<template>
  <div class="vap-page">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="vap-page-header">
      <h1>Travel Discrepancy Report</h1>
      <div class="btns-container">
        <VasionButton
          id="share"
          :classProp="'secondary'"
          :isDisabled="disableRunButtons || disableShareButton"
          :isSubmit="true"
          :ripple="true"
          @vasionButtonClicked="generatePDF"
        >
          Share
        </VasionButton>
        <VasionButton
          id="run-report"
          :classProp="'primary'"
          :isDisabled="disableRunButtons"
          :isSubmit="true"
          :ripple="true"
          @vasionButtonClicked="runDiscrepancyReport"
        >
          Run Report
        </VasionButton>
      </div>
    </div>
    <div class="vap-page-content">
      <form @submit.prevent="runDiscrepancyReport">
        <div class="setup-row">
        <VasionDropList
            v-slot="slotItem"
            v-model="objectToUse"
            class="row-field"
            :dataArray="attributeFormList"
            width="396"
            :title="`OBJECT TO USE`"
            :placeholder="`Select Object...`"
            type="plain-list"
            displayName="name"
            valueName="value"
            @input="isDirty = true"
          >
            {{ slotItem.item.name }}
        </VasionDropList>
        <VasionDropList
            v-slot="slotItem"
            v-model="uniqueIDField"
            class="row-field"
            :dataArray="fieldsList"
            width="396"
            :title="`UNIQUE ID`"
            :placeholder="`Select Field...`"
            type="plain-list"
            displayName="name"
            valueName="fieldID"
            @input="isDirty = true"
          >
            {{ slotItem.item.name }}
        </VasionDropList>
        </div>
        <div class="setup-row">
          <VasionInput
            id="travel-file"
            v-model="newTravelFile"
            class="row-field browse-input"
            title="TRAVEL FILE"
            inputType="top-white"
            name="travel-file"
            placeholder="Select File..."
            :required="false"
            :width="'396'"
          />
          <VasionButton
            id="travel-file-folder-button"
            class="browse-button"
            :classProp="'primary'"
            @vasionButtonClicked="toggleBrowseForTravelFileDialog();"
          >
            Browse
          </VasionButton>        
          <VasionFileUploadDialog
            :serverOptions="serverOptions"
            :show.sync="showUploadDialog"
            :acceptedFileTypes="['.csv']"
            :filePondLabel="filePondLabel"
            @allFilesUploaded="allFilesUploaded"
          />
          <VasionSnackbar
            id="import-snack"
            :showSnackbarBool.sync="showSnackbarBool"
            :snackbarImage="snackbarImage"
            :snackbarSubTitle="snackbarSubTitle"
            :snackbarTitle="snackbarTitle"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
// Import stylesheet
import 'vue-loading-overlay/dist/vue-loading.css';
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import { toBase64 } from '@/store/helperModules/storage.module'

export default {
  name: 'TheDiscrepancyReport',
  components: {
    Loading,
  },
  data: function () {
    return {
      fields: undefined,
      filePondLabel: `<span class="filepond-drag">Drag CSV File Here</span>
                <br/><br/>
                <span class="filepond-label-action">or click to upload your CSV File</span>`,
      isLoading: false,
      loaderBackgroundColor,
      loaderColor,
      newTravelFile: '',
      objectToUse: undefined,
      serverOptions: { process: this.processHandler },
      showSnackbarBool: false,
      showUploadDialog: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',      
      uniqueIDField: undefined,
      uploadedCSVFileString: '',
      discrepancyReportGUID: '',
    }
  },
  computed: {
    attributeFormList() {
      return this.$store.state.attributeForm.forms.map((f) => {
        return {
          name: f.name,
          value: f.value,
        }
      })
    },
    disableRunButtons() { return this.objectToUse === undefined || this.uniqueIDField === undefined || this.uploadedCSVFileString === '' },
    disableShareButton() { return this.discrepancyReportGUID === undefined || this.discrepancyReportGUID === '' },
    fieldsList() {
      if (this.fields === undefined) return []
      return this.fields.map((field) => {
        return {
          name: field.name,
          fieldID: field.fieldID,
        }
      })
    },
  },
  watch: {
    objectToUse: async function () {
      if (this.objectToUse === undefined || this.isLoading) return
      this.fields = await this.$store.dispatch('common/getIndexFieldsForForm', this.objectToUse.value)
      this.uniqueIDField = undefined
    },
  },
  async created() {
    this.isLoading = true
    await this.$store.dispatch('attributeForm/getForms')
    const promiseResults = await Promise.all([
      this.$store.dispatch('common/getConfig', 'DiscrepancyReportObject'),
      this.$store.dispatch('common/getConfig', 'DiscrepancyReportUniqueID'),
    ])
    this.objectToUse = this.attributeFormList.find((f) => f.value == promiseResults[0])
    if (this.objectToUse === undefined) {
      this.isLoading = false
      return
    }
    this.fields = await this.$store.dispatch('common/getIndexFieldsForForm', this.objectToUse.value)
    this.$nextTick(() => {
      this.uniqueIDField = this.fieldsList.find((f) => f.fieldID == promiseResults[1])
      this.isLoading = false
    })
  },
  methods: {
    allFilesUploaded() {
      this.showUploadDialog = false
    },
    async generatePDF() {
      this.isLoading = true
      const downloadResult = await this.$store.dispatch('reporting/downloadDiscrepancyReport', { GUID: this.discrepancyReportGUID, DownloadExcel: true })
      if (downloadResult) {
        // eslint-disable-next-line
        const fileDownload = require('js-file-download')
        fileDownload(downloadResult, 'TravelDiscrepancyReport.xlsx')
        await this.$amplitude.trackEvent('Report Shared', {
          'reportType': 'Discrepancy',
        })
      }
      else {
        this.showSnackbarBool = true
        this.snackbarImage = false
        this.snackbarTitle = "ERROR"
        this.snackbarSubTitle = "There was an error generating the report."
      }
      this.isLoading = false
    },
    processHandler: async function (fieldName, file, metadata, load, error, progress, abort) {
      if (file?.name.endsWith('.csv')) {
        this.uploadedCSVFileString = await toBase64(file)
        const base64String = this.uploadedCSVFileString.split('base64,')[1]
        if (!base64String){
          error(true)
          this.snackbarTitle = "ERROR"
          this.snackbarSubTitle = 'Empty CSV can not be used'
          this.showSnackbarBool = true
          return
        }
        progress(true, 0, 1024)
        load(file?.name)
        this.newTravelFile = file?.name
      } else {
        error(true)
        this.showSnackbarBool = true
        this.snackbarImage = false
        this.snackbarTitle = "ERROR"
        this.snackbarSubTitle = "Can only upload files that end in \".csv\"."
        return
      }
      return {
        abort: () => {
          abort();
        },
      };
    },
    async runDiscrepancyReport() {
      this.isLoading = true

      this.$store.dispatch('common/setConfig', { sConfigName: 'DiscrepancyReportObject', sConfigValue: this.objectToUse.value })
      this.$store.dispatch('common/setConfig', { sConfigName: 'DiscrepancyReportUniqueID', sConfigValue: this.uniqueIDField.fieldID })

      const payload = {
        ObjectId: this.objectToUse.value,
        UniqueFieldId: this.uniqueIDField.fieldID,
        CSVFileUpload: this.uploadedCSVFileString,
      }

      const resp = await this.$store.dispatch('reporting/runDiscrepancyReport', payload)
      this.discrepancyReportGUID = resp.GUID

      await this.$amplitude.trackEvent('Report Run', {reportType: 'Discrepancy'})

      const html = await this.$store.dispatch('reporting/downloadDiscrepancyReport', { GUID: this.discrepancyReportGUID, DownloadExcel: false })
      if (html === undefined)
      {
        this.isLoading = false
        this.showSnackbarBool = true
        this.snackbarImage = false
        this.snackbarTitle = "ERROR"
        this.snackbarSubTitle = "There was an error generating the preview for this report."
        return
      }
      var win = window.open();
      win.document.body.innerHTML = html;

      this.isLoading = false
    },
    toggleBrowseForTravelFileDialog() { this.showUploadDialog = !this.showUploadDialog },
  },
}
</script>
<style lang="scss" scoped>
  @import '@/assets/css/variables.scss';

  .setup-row {
    display: block;
    float: left;
    width: 100%;
    margin: 6px 0;

    > .vasion-droplist, .vasion-input {
      width: 310px;
      float: left;
      margin-right: 16px;
    }

    > .vasion-button {
      margin-left: 76px;
      margin-top: 18px;
    }
  }  

  .vasion-run-report-prompt {
     margin-top: 200px;
  }
</style>
